"use client";

import Image from "next/image";
import Link from "next/link";
import { useEffect, useState } from "react";

export default function Footer() {
  // Year read in useEffect to avoid year-boundary SSR/hydration mismatch (React #418).
  const [year, setYear] = useState<number | null>(null);
  useEffect(() => { setYear(new Date().getUTCFullYear()); }, []);

  return (
    <footer className="bg-[#0f1828] text-slate-200">
      <div className="mx-auto max-w-8xl px-6 py-14 md:py-16">
        {/* Top grid */}
        <div className="grid grid-cols-1 gap-10 md:grid-cols-[1.1fr_1fr_1fr_1fr] md:gap-12">
          {/* Brand / CTAs */}
          <div className="flex flex-col gap-6">
            <Link href="/" className="inline-flex items-center gap-3">
              {/* Use your dark logo and invert to show on dark bg */}
              {/* <Image
                src="/logo-dark.svg"
                alt="FeelGood"
                width={162}
                height={40}
                className="h-10 w-auto brightness-0 invert"
                priority
              /> */}
              <div className="text-3xl font-extrabold font-white">feelgood</div>
            </Link>

            <div className="flex flex-col gap-3">
              <Link
                href="/#"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-slate-100 px-6 py-3 text-slate-900 hover:bg-white transition"
              >
                Get Started
                <ArrowRight className="h-4 w-4" />
              </Link>

              <Link
                href="/login"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-400/30 px-6 py-3 text-slate-100 hover:bg-white/5 transition"
              >
                Login
                <UserIcon className="h-4 w-4" />
              </Link>
            </div>

            {/* Search */}
            <form
              onSubmit={(e) => e.preventDefault()}
              className="mt-2"
              role="search"
              aria-label="Site search"
            >
              <div className="flex overflow-hidden rounded-lg ring-1 ring-white/10">
                <div className="flex items-center gap-2 bg-white/5 px-3">
                  <SearchIcon className="h-4 w-4 text-slate-300" />
                </div>
                <input
                  className="w-full bg-white/5 px-3 py-2 text-sm placeholder:text-slate-400 outline-none"
                  placeholder="Search"
                />
                <button
                  className="bg-white/10 px-4 text-sm hover:bg-white/15"
                  type="submit"
                >
                  Search
                </button>
              </div>
            </form>
          </div>

          {/* Services */}
          <nav aria-label="Services" className="border-t border-white/10 pt-6 md:border-0 md:pt-0">
            <h3 className="text-sm font-semibold text-slate-300">Services</h3>
            <ul className="mt-4 space-y-3">
              <FooterLink href="/#">Lab Testing</FooterLink>
            </ul>
          </nav>

          {/* Learn more */}
          <nav aria-label="Learn more" className="border-t border-white/10 pt-6 md:border-0 md:pt-0">
            <h3 className="text-sm font-semibold text-slate-300">Learn More</h3>
            <ul className="mt-4 space-y-3">
              <FooterLink href="/#how-it-works">How It Works</FooterLink>
              <FooterLink href="/#testimonials">Patient Success</FooterLink>
              <FooterLink href="/support">Customer Support</FooterLink>
              <FooterLink href="/#faqs">FAQ’s</FooterLink>
              <FooterLink href="/support">Contact Us</FooterLink>
            </ul>
          </nav>

          {/* Contact */}
          <div className="border-t border-white/10 pt-6 md:border-0 md:pt-0">
            <h3 className="text-sm font-semibold text-slate-300">Contact</h3>
            <ul className="mt-4 space-y-3 text-slate-300">
              <li className="flex items-start gap-3">
                <PinIcon className="mt-1 h-4 w-4" />
                <span>
                  7901 4th St Suite 300
                  <br /> St Petersburg, FL 33702
                </span>
              </li>
              <li className="flex items-center gap-3">
                <PhoneIcon className="h-4 w-4" />
                <a href="tel:18886967176" className="hover:underline">
                  888-888-8888
                </a>
              </li>
              <li className="flex items-center gap-3">
                <MailIcon className="h-4 w-4" />
                <a href="mailto:help@feelgood.health" className="hover:underline">
                  help@feelgood.health
                </a>
              </li>
            </ul>

            {/* Badges */}
            <div className="mt-6 flex items-center gap-4">
              <Image
                src="/images/hipaa-badge.svg"
                alt="HIPAA Compliant"
                width={56}
                height={56}
                className="h-14 w-14 object-contain"
              />
              <Image
                src="/images/labassist-badge.png"
                alt="Lab Assist Certified"
                width={56}
                height={56}
                className="h-14 w-14 object-contain"
              />
            </div>

            {/* Socials */}
            <div className="mt-6 flex gap-4">
              <SocialCircle href="https://facebook.com">
                <FacebookIcon className="h-4 w-4" />
              </SocialCircle>
              <SocialCircle href="https://instagram.com">
                <InstagramIcon className="h-4 w-4" />
              </SocialCircle>
              <SocialCircle href="https://youtube.com">
                <YouTubeIcon className="h-4 w-4" />
              </SocialCircle>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="my-10 h-px w-full bg-white/10" />

        {/* Disclaimers */}
        <div className="space-y-4 text-xs leading-relaxed text-slate-400">
          <p>
            FeelGood.Health is a healthcare technology platform, not a medical practice, laboratory, or provider of clinical services. All blood testing, laboratory analysis, physician oversight, and related medical services are performed exclusively by independent third-party providers, including Labcorp and licensed physicians or medical networks. These providers determine their own fees, operational policies, and clinical protocols.</p>

          <p>Access to laboratory testing is subject to state regulations. Certain states, including but not limited to New York and New Jersey, impose additional rules around ordering, billing, and administering laboratory tests. As a result, individuals testing in these states may incur separate charges billed directly by Labcorp or the applicable medical provider at the time of service. FeelGood.Health does not control or mark up any fees set by third-party laboratories or medical groups.</p>

          <p>FeelGood.Health does not practice medicine, provide clinical advice, diagnose conditions, interpret lab results, or recommend treatment. Any information generated by our platform—whether educational, AI-assisted, or data-driven—is for general informational purposes only and is not a substitute for professional judgment from a licensed clinician. You should consult your primary care physician or another qualified provider regarding any questions about laboratory findings, symptoms, or health decisions.</p>

          <p>Membership pricing reflects technology, platform, and service-related fees charged by FeelGood.Health and may include limited prepaid access to certain laboratory services fulfilled by third parties. A detailed breakdown of included services and any additional fees is available upon request. Some items, panels, or follow-up services may require separate payment and are not included in standard pricing.</p>

          <p>FeelGood.Health does not provide tax, legal, or financial guidance. Eligibility rules for HSA/FSA reimbursement vary by plan and may change over time. You should confirm coverage and compliance details with your plan administrator, employer, or a qualified tax advisor. FeelGood.Health is not responsible for any changes in regulatory guidance or plan-specific requirements that may affect your benefits.</p>

          <p>By providing your phone number or email address, you consent to receive communications from FeelGood.Health, which may include automated messages for appointment reminders, product updates, or service notifications. Message and data rates may apply. Message frequency may vary. You may opt out at any time.</p>

          <p>For full terms, limitations, privacy practices, and other important information, please review our Terms of Service and Privacy Policy.</p>
        </div>

        {/* Bottom bar */}
        <div className="mt-10 flex flex-col items-start justify-between gap-4 border-t border-white/10 pt-6 text-sm text-slate-400 md:flex-row md:items-center">
          <p>© {year ?? ''} FeelGood. All rights reserved.</p>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
            <Link href="/refund-policy" className="hover:underline">
              Refund Policy
            </Link>
            <Link href="/privacy-policy" className="hover:underline">
              Privacy Policy
            </Link>
            <Link href="/terms" className="hover:underline">
              Terms of Service
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}

/* ---------- small helpers ---------- */

function FooterLink({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <li>
      <Link href={href} className="hover:text-white/90 transition">
        {children}
      </Link>
    </li>
  );
}

function SocialCircle({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <Link
      href={href}
      className="grid h-10 w-10 place-items-center rounded-full border border-white/15 bg-white/5 text-slate-100 hover:bg-white/10"
      aria-label="Social link"
    >
      {children}
    </Link>
  );
}

/* ---------- icons (inline SVGs) ---------- */

function ArrowRight(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} {...props}>
      <path d="M5 12h14M13 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
function UserIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} {...props}>
      <path d="M12 12a5 5 0 1 0-5-5 5 5 0 0 0 5 5Zm7 8a7 7 0 0 0-14 0" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
function SearchIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} {...props}>
      <path d="m21 21-4.3-4.3M10 18a8 8 0 1 1 0-16 8 8 0 0 1 0 16Z" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
function PinIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} {...props}>
      <path d="M12 22s7-4.4 7-11a7 7 0 0 0-14 0c0 6.6 7 11 7 11Z" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx="12" cy="11" r="2" />
    </svg>
  );
}
function PhoneIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} {...props}>
      <path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6 19.8 19.8 0 0 1-3-8.6A2 2 0 0 1 4 2h3a2 2 0 0 1 2 1.7c.2 1.2.6 2.3 1 3.4a2 2 0 0 1-.5 2.1L8.1 10a16 16 0 0 0 6 6l.8-1.4a2 2 0 0 1 2.1-.5c1.1.4 2.2.8 3.4 1a2 2 0 0 1 1.6 1.8Z" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
function MailIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} {...props}>
      <path d="M4 4h16v16H4z" />
      <path d="m22 6-10 7L2 6" />
    </svg>
  );
}
function FacebookIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
      <path d="M13 11h3.8l-.5 3H13v10h-3V14H7v-3h3V8.5C10 5.6 11.6 4 14.9 4H18v3h-2.2c-1.6 0-1.8.6-1.8 1.7V11Z" />
    </svg>
  );
}
function InstagramIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
      <path d="M7 2h10a5 5 0 0 1 5 5v10a5 5 0 0 1-5 5H7a5 5 0 0 1-5-5V7a5 5 0 0 1 5-5Zm5 5a5 5 0 1 0 0 10 5 5 0 0 0 0-10Zm6.5-.8a1.2 1.2 0 1 0 0 2.4 1.2 1.2 0 0 0 0-2.4Z" />
    </svg>
  );
}
function YouTubeIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
      <path d="M23 7.5a4 4 0 0 0-2.8-2.8C18.5 4 12 4 12 4s-6.5 0-8.2.7A4 4 0 0 0 1 7.5C.7 9.2.7 12 .7 12s0 2.8.3 4.5A4 4 0 0 0 3 19.8C4.7 20.5 12 20.5 12 20.5s6.5 0 8.2-.7a4 4 0 0 0 2.8-2.8c.3-1.7.3-4.5.3-4.5s0-2.8-.3-4.5ZM10 15.3V8.7L16 12l-6 3.3Z" />
    </svg>
  );
}
